<template>
  <div>
    <label class="text-left block">
      <slot></slot>
    </label>
    <div class="flex flex-col">
      <label class="mr-2 pr-2 mb-2.5" v-for="(val, key) in options" :key="key">
        <input
          type="checkbox"
          :value="key"
          v-model="formValues.needsList"
          @input="updateData(key)"
          :disabled="isDisabled(key) || disabled"
        />
        {{ val }}
      </label>
      <label
        class="text-sm font-bold mb-2.5">
        <input
          :disabled="disabled"
          type="checkbox"
          v-model="checkBoxes.solo"
          @input="updateData('infoSolo'); removeEnclosedVehicleWithSolo()"
        />
        Solo Transport (No other students allowed in vehicle.)
      </label>
      <label
        v-if="isSoloChecked()"
        class="text-sm font-bold ml-4 mb-2.5">
        <input
          :disabled="disabled"
          type="checkbox"
          v-model="formValues.enclosedVehicle"
          @change="updateEnclosedVehicle()"
        />
        Enclosed/Barrier Vehicle
      </label>
      <label
        class="text-sm font-bold mb-2.5">
        <input
          :disabled="disabled"
          type="checkbox"
          v-model="checkBoxes.aide"
          @input="updateData('infoAide')"
        />
        Aide/Para
      </label>
      <label
        v-if="isAideChecked()"
        class="text-sm font-bold ml-4 mb-2.5">
        <input
          :disabled="disabled"
          type="checkbox"
          v-model="formValues.aideOnetoOne"
          @change="updateAide()"
        />
        This student require an Aide/Para assigned exclusively to them (one-to-one)
      </label>
      <div class="flex flex-row">
        <label 
          class="text-sm font-bold mb-2.5">
          <input 
            :disabled="disabled"
            type="checkbox"
            v-model="checkBoxes.safetyVest"
            @input="updateData('infoSafetyVest')"
          />
          Safety Vest
        </label>
      </div>
      <div>
        <div v-if="vestChecked()">
          <label class="text-sm font-bold ml-4 mb-2.5">
            Do you need Collaborative Student Transportation to provide the safety vest?
          </label>
          <ValidationProvider :rules="`required`" name="CST vest provided" v-slot="{ errors }">
            <label class="text-sm font-bold ml-4 mb-2.5">
              <label class="mr-2">
                <input 
                  :disabled="disabled"
                  type="radio"
                  name="provided"
                  v-model="formValues.vestInfo.needVestProvided"
                  :value="true"
                  @input="provideCheck(true)"
                />
                Yes
              </label>
              <label>
                <input 
                  :disabled="disabled"
                  type="radio"
                  name="provided"
                  v-model="formValues.vestInfo.needVestProvided"
                  :value="false"
                  @input="provideCheck(false)"
                />
                No
              </label>
            </label>
            <span
              class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
              name="error-text">
              {{ errors[0] }}
            </span>
          </ValidationProvider>
          <div v-if="isProvided === true" class="ml-4 mb-2.5">
            <p class="font-semibold">What size vest does the student require?</p>
            <ValidationProvider :rules="`required`" name="vest size" v-slot="{ errors }">
              <label
                v-for="size in adjustedVestSizeOptions"
                :key="size.value"
                class="mr-4 text-sm font-medium">
                <input 
                  :disabled="disabled"
                  type="radio"
                  name="vestSize"
                  v-model="formValues.vestInfo.vestSize"
                  :value="size.value"
                />
                {{ size.label }}
              </label>
              <span
                class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                name="error-text">
                {{ errors[0] }}
              </span>
            </ValidationProvider>
            <p class="text-sm mt-2">
              View sizing guide 
              <a href="https://ezonpro.com/wp-content/uploads/2023/11/Sizing-Chart.2024.pdf" target="_blank" class="text-blue-600 underline">here</a>.
            </p>
          </div>
          <div v-if="isProvided === false" class="ml-4 mb-2.5">
            <ValidationProvider :rules="`required`" name="manufacturer name" v-slot="{ errors }">
              <TextField
                class="p-2 w-full"
                :disabled="disabled"
                placeholder="Enter manufacturer name"
                v-model="formValues.vestInfo.manufacturer">
                Safety Vest Manufacturer Name
              </TextField>
              <span
                class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                name="error-text">
                {{ errors[0] }}
              </span>
            </ValidationProvider>
            <ValidationProvider :rules="`required`" name="model/item number" v-slot="{ errors }">
              <TextField
                class="p-2 w-full"
                :disabled="disabled"
                placeholder="Enter model or item number"
                v-model="formValues.vestInfo.modelNumber">
                Safety Vest Model/Item Number
              </TextField>
              <span
                class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                name="error-text">
                {{ errors[0] }}
              </span>
            </ValidationProvider>
            <div class="pl-2">
              <p class="font-semibold">What size vest?</p>
              <ValidationProvider name="vest size" v-slot="{ errors }">
                <label
                  v-for="size in adjustedVestSizeOptions"
                  :key="size.value"
                  class="mr-4 text-sm font-medium">
                  <input
                    :disabled="disabled"
                    type="radio"
                    name="vestSize"
                    v-model="formValues.vestInfo.vestSize"
                    :value="size.value"
                  />
                  {{ size.label }}
                </label>
                <span
                  class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                  name="error-text">
                  {{ errors[0] }}
                </span>
              </ValidationProvider>
            </div>
          </div>
        </div>
        <div
          v-if="showModal" 
          class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
        >
          <div class="bg-white p-6 rounded shadow-lg w-1/3">
            <h2 class="text-xl font-bold mb-4">Important Information</h2>
            <p class="mb-4">
              The school is responsible for ensuring the family has the vest for the first day of scheduled transportation with CST.
            </p>
            <p class="mb-4">
              It is also the school's responsibility to recover the safety vest and any equipment the school owns from the family.
            </p>
            <button 
              @click="closeModal" 
              class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TextField from './formComponents/TextField.vue'

import DropdownLists from './DropdownLists.json'

export default {
  components:{
    TextField,
  },
  props: {
    defaultValues: undefined,
    defaultAide: undefined,
    disabled: Boolean,
  },
  data() {
    return {
      formValues: {
        needsList: [],
        vestInfo: {
          needVestProvided: undefined,
          vestSize: undefined,
          manufacturer: "",
          modelNumber: "",
        },
        aide: '',
        aideOnetoOne: false, 
        enclosedVehicle: false,
      },
      disableModal: false,

      isProvided: undefined,
      showModal: false,
      checkBoxes: {
        safetyVest: false,
        aide: false,
        solo: false,
      },
      specialNeedsOptions: DropdownLists.specialNeeds,
      vestSizeOptions: ['XS', 'S', 'M', 'L', 'XL'],
      hover: false,
    }
  },
  methods: {
    tomorrow() {
      const today = new Date()
      const tomorrow = new Date(today)
      tomorrow.setDate(tomorrow.getDate() + 1)

      var month = tomorrow.getMonth() + Number(1)
      if (month < 10) {
        month = '0' + month
      }
      var date = tomorrow.getDate()
      if (date < 10) {
        date = '0' + date
      }
      return tomorrow.getFullYear() + '-' + month + '-' + date
    },
    closeModal() {
      this.showModal = false
    },
    setDefault() {
      if (this.defaultValues?.needsList.length > 0) {
        this.disableModal = true

        this.formValues.vestInfo = {}
        if (this.defaultValues instanceof Array) {
          this.formValues.needsList = this.defaultValues
        }
        else {
          this.formValues = {...this.defaultValues}
          this.formValues.needsList = this.defaultValues.needsList ? [...this.defaultValues.needsList] : []
          this.formValues.vestInfo = this.defaultValues.vestInfo ? {...this.defaultValues.vestInfo} : {}
        }

        this.formValues.aide = this.defaultAide !== undefined ? this.defaultAide : ''
        this.formValues.aideOnetoOne = this.defaultAide === 'A1:1' ? true : false
        this.formValues.enclosedVehicle = this.formValues.needsList.includes('infoEnclosedVehicle')

        this.checkBoxes.safetyVest = this.formValues.needsList.includes('infoSafetyVest')
        this.checkBoxes.aide = this.formValues.needsList.includes('infoAide')
        this.checkBoxes.solo = this.formValues.needsList.includes('infoSolo')
      }
    },
    provideCheck(val) {
      this.formValues.vestInfo.needVestProvided = val
      this.isProvided = val

      if (this.disableModal) {
        this.disableModal = false
        return
      }

      if (val === false) {
        this.showModal = true
      }
    },
    updateData(val) {
      if (this.formValues.needsList.includes(val)) {
        let index = this.formValues.needsList.indexOf(val)
        this.formValues.needsList.splice(index, 1)
      }
      else {
        this.formValues.needsList.push(val)
      }

      this.$emit('input', this.formValues)
    },
    isDisabled(option) {
      return (option === 'infoBoosterSeat' && this.formValues.needsList.includes('infoCarSeat')) ||
             (option === 'infoCarSeat' && this.formValues.needsList.includes('infoBoosterSeat'))
    },
    removeEnclosedVehicleWithSolo() {
      if (this.formValues.needsList.includes('infoEnclosedVehicle')) {
        let index = this.formValues.needsList.indexOf('infoEnclosedVehicle')
        this.formValues.needsList.splice(index, 1)
      }
    },
    isSoloChecked() {
      if (this.formValues.needsList) {
        if (this.formValues.needsList?.includes('infoSolo')) {
          return true
        } else {
          return false
        }
      }
      return false
    },
    vestChecked() {
      return this.formValues.needsList.includes('infoSafetyVest')
    },
    isAideChecked() {
      if (this.formValues.needsList) {
        if (this.formValues.needsList.includes('infoAide')) {
          return true
        } else {
          this.$set(this.formValues, 'aide', '')
          return false
        }
      }
      return false
    },
    updateEnclosedVehicle() {
      const index = this.formValues.needsList.indexOf('infoEnclosedVehicle')
      const exists = index > -1
      if (exists) {
        if (!this.formValues.enclosedVehicle) {
          this.formValues.needsList.splice(index, 1)
        }
      } else {
        if (this.formValues.enclosedVehicle) {
          this.formValues.needsList.push('infoEnclosedVehicle')
        }
      }
      //this.updateData()
    },
    updateAide() {
      this.formValues.aide = this.formValues.aideOnetoOne ? 'A1:1' : 'A'
    },
    updateValue() {
      this.$emit('input', this.formValues)
    },
  },
  computed: {
    options() {
      return {...this.specialNeedsOptions}
    },
    adjustedVestSizeOptions() {
      let retArray = this.vestSizeOptions.map(size => ({
        label: size,
        value: size,
      }))
      if (this.isProvided === false) {
        return [
          {
            label: "N/A",
            value: undefined,
          },
          ...retArray
        ]
      }
      return retArray
    }
  },
  watch: {
    defaultValues() {
      this.setDefault()
    },
    'formValues.aide'(val) {
      if (val == "") {
        this.formValues.aideOnetoOne = false
      }
    },
    'formValues.needsList'(val) {
      if (!val.includes('infoSolo')) {
        this.formValues.enclosedVehicle = false
      }
      this.updateAide()
    },
    formValues() {
      this.updateValue()
    },
    'formValues.vestInfo.needVestProvided'(newValue) {
      this.isProvided = newValue
      if (this.disableModal) {
        this.disableModal = false
        return
      }

      if (newValue === false) {
        this.showModal = true
      }
    },
  },
  mounted() {
    this.setDefault()
    delete this.options.infoEnclosedVehicle
    delete this.options.infoAide
    delete this.options.infoSolo
    delete this.options.infoSafetyVest
  }
}
</script>
